// Theme resolution: keep the pane in step with Outlook's appearance.
//
// Two signals, in priority order:
//
//   1. Office.context.officeTheme — Outlook's OWN setting. This is the one that
//      matters, because Outlook has a dark-mode toggle independent of the OS, and
//      the pane is an iframe with its own document: CSS prefers-color-scheme
//      reports the OS/browser preference, so a user with a light OS and dark
//      Outlook would otherwise get a glaring white pane.
//   2. The LAST THEME OUTLOOK REPORTED, remembered in localStorage. A far better
//      predictor of what Outlook is about to say than the OS is, because it IS
//      what Outlook said last time on this device.
//   3. prefers-color-scheme — only when the host exposes no theme and we've never
//      seen one (plain-browser dev, or a first-ever launch).
//
// The ordering used to be Office -> OS, and it caused a visible flash: at first
// paint the host isn't ready, so getOfficeThemeMode() returns null and a dark-OS
// user with light Outlook got a full dark pane that snapped to light a moment
// later. The OS preference is simply not evidence about Outlook's own toggle, and
// treating it as the immediate fallback made every mismatched user pay for it on
// every launch. See index.html for the pre-paint half of this fix.
//
// We classify by the LUMINANCE of the background colour Outlook reports rather
// than by any theme name — the names vary across hosts and versions.

import { getOfficeThemeMode, onHostReady, onOfficeThemeChanged } from "./office/office";

export type ThemeMode = "light" | "dark";

/** Shared with the inline pre-paint script in index.html — keep them in step. */
export const THEME_CACHE_KEY = "baymark-hr-theme";

function prefersDark(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
}

/** The last mode Outlook itself reported on this device, if any. */
export function readCachedMode(): ThemeMode | null {
  try {
    const v = localStorage.getItem(THEME_CACHE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
}

/** Only ever called with a mode the HOST reported. Caching an OS-derived guess
 *  would make a wrong guess sticky across launches. */
function writeCachedMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_CACHE_KEY, mode);
  } catch {
    /* storage unavailable — caching is an optimisation, not a requirement */
  }
}

/** Outlook's theme if it tells us, else what it told us last time, else the OS. */
export function resolveThemeMode(): ThemeMode {
  return getOfficeThemeMode() ?? readCachedMode() ?? (prefersDark() ? "dark" : "light");
}

/** Write the mode where CSS can see it. `color-scheme` is set too so the host
 *  gives us dark scrollbars, form controls and caret rather than light ones on a
 *  dark pane. */
export function applyThemeMode(mode: ThemeMode): void {
  const root = document.documentElement;
  // Always clear the pending flag, even when the mode is unchanged: the pane is
  // held invisible while pending, so leaving it set would hide the UI forever on
  // the path where the cached mode already matched what the host reported.
  root.removeAttribute("data-theme-pending");
  if (root.dataset.theme === mode) return;
  root.dataset.theme = mode;
  root.style.colorScheme = mode;
}

/** Start following the host theme. Returns a cleanup function.
 *
 *  Re-resolves on: OfficeThemeChanged (when the host supports it), the OS media
 *  query changing, the host becoming ready after first paint, and the pane
 *  regaining visibility/focus — the last one is the belt-and-braces path for hosts
 *  that never fire a theme event, mirroring how item changes are handled. */
export function startThemeSync(onChange?: (mode: ThemeMode) => void): () => void {
  let current: ThemeMode | null = null;

  const apply = () => {
    const fromHost = getOfficeThemeMode();
    const mode = fromHost ?? readCachedMode() ?? (prefersDark() ? "dark" : "light");
    // Remember only what the HOST said, so the next launch starts from evidence
    // rather than from the OS. This is what makes the flash a once-per-device
    // event instead of a once-per-launch one.
    if (fromHost) writeCachedMode(fromHost);
    applyThemeMode(mode);
    if (mode !== current) {
      current = mode;
      onChange?.(mode);
    }
  };

  apply();

  // Backstop: if the host never reports a theme (plain browser, or a host build
  // that doesn't expose officeTheme), nothing else would clear data-theme-pending
  // and the pane would stay invisible. Reveal on the best guess we have.
  const revealTimer = setTimeout(() => {
    if (document.documentElement.hasAttribute("data-theme-pending")) {
      console.log("[theme] host reported no theme within 600ms — revealing on the fallback");
      applyThemeMode(resolveThemeMode());
    }
  }, 600);

  const unsubscribeTheme = onOfficeThemeChanged(apply);
  // The host often isn't ready at first paint, so officeTheme is absent and we
  // start from the OS. Re-resolve once it arrives.
  onHostReady(apply);

  const mq = typeof matchMedia === "function" ? matchMedia("(prefers-color-scheme: dark)") : null;
  mq?.addEventListener?.("change", apply);

  const onVisible = () => {
    if (document.visibilityState === "visible") apply();
  };
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", onVisible);

  return () => {
    clearTimeout(revealTimer);
    unsubscribeTheme();
    mq?.removeEventListener?.("change", apply);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("focus", onVisible);
  };
}
